import * as React from "react";
import { Box, Button, Container, Paper, Typography } from "@mui/material";
import { useAuth0 } from "@auth0/auth0-react";
import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import AuthErrorScreen from "../components/AuthErrorScreen";
import { reportAuthFailure } from "../utils/sentry";
import {
  clearPersistedAuthError,
  getAuthErrorFromSearchParams,
  persistAuthError,
  readPersistedAuthError,
} from "../utils/authErrors";

const NO_ADMIN_MSG =
  "Your account is signed in but does not have admin access for this app.";

function resolveAuthError(searchParams, locationState, auth0SdkError) {
  const fromUrl = getAuthErrorFromSearchParams(searchParams);
  if (fromUrl) return fromUrl;
  if (locationState?.error) return locationState.error;
  if (locationState?.reason) return locationState.reason;
  if (auth0SdkError?.message) return auth0SdkError.message;
  return readPersistedAuthError();
}

export default function SignIn() {
  const {
    loginWithRedirect,
    logout,
    isAuthenticated,
    isLoading,
    getIdTokenClaims,
    error: auth0SdkError,
  } = useAuth0();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const [displayError, setDisplayError] = useState(() =>
    resolveAuthError(searchParams, location.state, auth0SdkError)
  );
  const [checkingClaim, setCheckingClaim] = useState(false);
  const reportedRef = useRef(null);
  const urlErrorHandled = useRef(false);
  const claimCheckDone = useRef(false);

  useEffect(() => {
    const fromUrl = getAuthErrorFromSearchParams(searchParams);
    if (!fromUrl || urlErrorHandled.current) return;
    urlErrorHandled.current = true;
    persistAuthError(fromUrl);
    setDisplayError(fromUrl);
    if (searchParams.get("error") || searchParams.get("error_description")) {
      navigate(location.pathname, { replace: true, state: { error: fromUrl } });
    }
  }, [searchParams, location.pathname, navigate]);

  useEffect(() => {
    const err = resolveAuthError(searchParams, location.state, auth0SdkError);
    if (err) setDisplayError(err);
  }, [searchParams, location.state, auth0SdkError]);

  useEffect(() => {
    if (!displayError || reportedRef.current === displayError) return;
    reportedRef.current = displayError;
    persistAuthError(displayError);
    reportAuthFailure("sign_in", displayError, {
      errorCode: searchParams.get("error") || undefined,
      fromUrl: Boolean(getAuthErrorFromSearchParams(searchParams)),
    });
  }, [displayError, searchParams]);

  // Already signed in at Auth0 — check admin claim once; stay on this page if denied.
  useEffect(() => {
    if (isLoading || displayError || !isAuthenticated || claimCheckDone.current) return;

    let cancelled = false;
    claimCheckDone.current = true;
    setCheckingClaim(true);

    (async () => {
      try {
        const claims = await getIdTokenClaims();
        if (cancelled) return;

        const adminClaim = claims?.["https://brytebridge.com/penn-station/claims/is_admin"];
        if (adminClaim) {
          clearPersistedAuthError();
          navigate("/dashboard", { replace: true });
          return;
        }

        persistAuthError(NO_ADMIN_MSG, { isAdmin: false });
        reportAuthFailure("admin_claim", NO_ADMIN_MSG, { isAdmin: false });
        setDisplayError(NO_ADMIN_MSG);
      } catch (error) {
        if (cancelled) return;
        const reason =
          error?.message ||
          "Could not verify admin permissions. Check your connection and try again.";
        persistAuthError(reason, { thrown: true });
        reportAuthFailure("admin_claim", reason, { thrown: true });
        setDisplayError(reason);
      } finally {
        if (!cancelled) setCheckingClaim(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, isLoading, displayError, navigate, getIdTokenClaims]);

  useEffect(() => {
    if (!isAuthenticated) {
      claimCheckDone.current = false;
    }
  }, [isAuthenticated]);

  const signInReturnTo = `${window.location.origin}/sign-in`;

  const handleSignIn = () => {
    if (!isAuthenticated) {
      clearPersistedAuthError();
      setDisplayError(null);
      claimCheckDone.current = false;
    }

    loginWithRedirect({
      authorizationParams: { prompt: "login" },
      appState: { returnTo: "/dashboard" },
    }).catch((error) => {
      const message = error?.message || "Could not start sign in";
      persistAuthError(message);
      setDisplayError(message);
      reportAuthFailure("sign_in_redirect", message);
    });
  };

  const handleSignOut = () => {
    persistAuthError(displayError || NO_ADMIN_MSG);
    logout({
      logoutParams: { returnTo: signInReturnTo },
    });
  };

  if (displayError) {
    return (
      <AuthErrorScreen
        title="Sign in failed"
        message={displayError}
        primaryLabel={isAuthenticated ? "Sign out and try another account" : "Try again"}
        onPrimary={isAuthenticated ? handleSignOut : handleSignIn}
        secondaryLabel={isAuthenticated ? "Try again (same account)" : undefined}
        onSecondary={isAuthenticated ? handleSignIn : undefined}
      />
    );
  }

  if (isLoading || checkingClaim) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
        }}
      >
        <Typography color="text.secondary">Checking sign-in status…</Typography>
      </Box>
    );
  }

  if (isAuthenticated) {
    return null;
  }

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
        }}
      >
        <Paper sx={{ p: 4, width: "100%", textAlign: "center" }}>
          <Typography variant="h5" gutterBottom fontWeight="bold">
            Portal Admin
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Sign in with your BryteBridge account to continue.
          </Typography>
          <Button variant="contained" size="large" onClick={handleSignIn} fullWidth>
            Sign in
          </Button>
        </Paper>
      </Box>
    </Container>
  );
}
